import { useMemo, useRef, useState } from 'react';
import { COUNTRIES, countryName } from '../lib/countries';
import { FlagIcon } from './FlagIcon';
import './CountrySelect.css';

/**
 * Searchable country combobox. A text input that filters the country list as
 * you type (flag + name), with keyboard nav. Stores the ISO alpha-2 code.
 * Used in onboarding + profile edit. Pass the host's input class so it matches
 * the surrounding form (e.g. "onb__select" / "profile__select").
 */
interface Props {
  value: string;
  onChange: (code: string) => void;
  className?: string;
  ariaLabel?: string;
  id?: string;
  /** Narrow trigger that shows ONLY the selected flag; the open list still
      shows flag + full country name, and typing still searches. */
  compact?: boolean;
}

export function CountrySelect({ value, onChange, className, ariaLabel = 'Country', id, compact = false }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const blurTimer = useRef<number | null>(null);

  // Plain name in the text input (a flag image can't live inside an <input>);
  // the flag shows on the options + wherever the country is displayed.
  const selectedLabel = value ? countryName(value) : '';

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q));
  }, [query]);

  function choose(code: string) {
    onChange(code);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && matches[active]) { e.preventDefault(); choose(matches[active].code); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  // Compact closed state: the flag image overlays an empty input (a flag
  // can't live INSIDE an <input>), so only the flag shows until it opens.
  const flagOnly = compact && !open && !!value;
  // Empty compact box: a flag silhouette hint so it's obvious this narrow box
  // is where the country goes (there's no room for a "Country" placeholder).
  const flagHint = compact && !open && !value;

  return (
    <div className={`country-select${compact ? ' country-select--compact' : ''}`}>
      <input
        id={id}
        className={className}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        autoComplete="off"
        placeholder={compact ? '' : 'Country'}
        value={open ? query : (flagOnly ? '' : selectedLabel)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => { setOpen(true); setQuery(''); setActive(0); }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Delay so an option's mousedown registers before we close.
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
      />
      {flagOnly && (
        <span className="country-select__flag" aria-hidden="true">
          <FlagIcon code={value} />
        </span>
      )}
      {flagHint && (
        <span className="country-select__hint" aria-hidden="true" title="Pick your country">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
        </span>
      )}
      {open && matches.length > 0 && (
        <ul className="country-select__list" role="listbox">
          {matches.map((c, i) => (
            <li
              key={c.code}
              role="option"
              aria-selected={c.code === value}
              className={`country-select__opt${i === active ? ' is-active' : ''}${c.code === value ? ' is-selected' : ''}`}
              // mousedown (not click) so it fires before the input blur closes the list
              onMouseDown={(e) => { e.preventDefault(); choose(c.code); }}
              onMouseEnter={() => setActive(i)}
            >
              <FlagIcon code={c.code} /> {c.name}
            </li>
          ))}
        </ul>
      )}
      {open && matches.length === 0 && (
        <ul className="country-select__list" role="listbox">
          <li className="country-select__empty">No matches</li>
        </ul>
      )}
    </div>
  );
}
